// Página pública de pagamento — /pay/<slug>
// Sem autenticação. Aluno (ou qualquer pessoa com o link) abre e vê:
// logo, escola, valor, descrição, Pix copia-e-cola, QR e estado.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Sparkles,
  XCircle
} from "lucide-react";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";
import {
  BillingAddressFields,
  emptyBillingAddress,
  validateBillingAddress as validateAddrClient,
  type BillingAddress
} from "@app/ui/components/billing-address-fields";

interface Branding {
  logoUrl: string | null;
  colorPrimary: string | null;
  colorSecondary: string | null;
  fontFamily: string | null;
  buttonRadius: string | null;
  allowDarkLight: boolean;
}

interface ChargeData {
  slug: string;
  status: "pending" | "paid" | "expired" | "canceled";
  pacoteNome: string;
  numAulas: number;
  valorCents: number;
  disciplinaName: string;
  centroCusto: string | null;
  txid: string;
  pixBrcode: string | null;
  createdAt: string;
  paidAt: string | null;
  expiresAt: string | null;
  canceledAt: string | null;
  aluno: { name: string; email: string };
  company: {
    id: string;
    name: string;
    branding: Branding;
  };
}

interface PaymentMethods {
  pixEnabled: boolean;
  ccEnabled: boolean;
  ccProvider: "pix_manual" | "stripe" | "mercadopago" | "mock";
}

function getSlugFromPath(): string | null {
  if (typeof window === "undefined") return null;
  // /pay/<slug>/  (Vite base)
  const m = window.location.pathname.match(/\/pay\/([^/]+)/);
  return m?.[1] ?? null;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function App() {
  const slug = useMemo(() => getSlugFromPath(), []);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [charge, setCharge] = useState<ChargeData | null>(null);
  const [methods, setMethods] = useState<PaymentMethods>({
    pixEnabled: true,
    ccEnabled: false,
    ccProvider: "mock"
  });

  async function load() {
    if (!slug) {
      setErr("Link inválido.");
      setPhase("error");
      return;
    }
    try {
      const r = await fetch(`/api/pay?slug=${encodeURIComponent(slug)}`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setCharge(data.charge);
      if (data.methods) setMethods(data.methods);
      setPhase("ready");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Aplica cores da branding via CSS variable. Default = nossas cores.
  const styleVars: any = useMemo(() => {
    const b = charge?.company.branding;
    return {
      "--brand-primary": b?.colorPrimary || "#8b5cf6",
      "--brand-secondary": b?.colorSecondary || "#6366f1",
      "--brand-radius": b?.buttonRadius || "0.5rem"
    };
  }, [charge]);

  if (phase === "loading") {
    return (
      <div class="grid min-h-screen place-items-center bg-background text-foreground">
        <div class="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin text-primary" />
          Carregando link de pagamento…
        </div>
      </div>
    );
  }

  if (phase === "error" || !charge) {
    return (
      <div class="grid min-h-screen place-items-center bg-background text-foreground p-4">
        <div class="w-full max-w-md rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center">
          <AlertCircle className="mx-auto mb-2 size-8 text-destructive" />
          <h1 class="text-lg font-bold">Link inválido</h1>
          <p class="mt-1 text-sm text-muted-foreground">{err || "Cobrança não encontrada."}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      class="min-h-screen bg-background text-foreground"
      style={styleVars}
    >
      <div class="mx-auto flex min-h-screen max-w-xl flex-col px-4 py-6">
        {/* Header — logo escola + plataforma */}
        <header class="mb-5 flex items-center justify-between">
          <div class="flex items-center gap-3">
            {charge.company.branding.logoUrl ? (
              <img
                src={charge.company.branding.logoUrl}
                alt={charge.company.name}
                class="h-10 w-auto"
              />
            ) : (
              <div
                class="grid size-10 place-items-center rounded-md text-white"
                style={{ background: "var(--brand-primary)" }}
              >
                <Sparkles className="size-5" />
              </div>
            )}
            <div>
              <div class="text-base font-bold leading-tight">
                {charge.company.name}
              </div>
              <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
                Cobrança via Simulador
              </div>
            </div>
          </div>
        </header>

        <StatusCard charge={charge} />

        {/* Resumo do que está sendo cobrado */}
        <section class="mt-4 rounded-md border border-border bg-card/60 p-5">
          <h2 class="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Cobrança
          </h2>
          <dl class="space-y-1.5 text-sm">
            <Row label="Para" value={charge.aluno.name} />
            <Row label="Pacote" value={charge.pacoteNome} />
            <Row label="Disciplina" value={charge.disciplinaName} />
            <Row label="Aulas" value={String(charge.numAulas)} />
            <Row
              label="Valor"
              value={formatBRL(charge.valorCents)}
              valueClass="text-lg font-extrabold tabular-nums"
              style={{ color: "var(--brand-primary)" }}
            />
          </dl>
        </section>

        {/* Pix block — só pendente */}
        {charge.status === "pending" &&
          methods.pixEnabled &&
          charge.pixBrcode && (
            <PixBlock pixBrcode={charge.pixBrcode} txid={charge.txid} />
          )}

        {/* Card block — pendente + ccEnabled */}
        {charge.status === "pending" && methods.ccEnabled && (
          <CardBlock slug={charge.slug} onPaid={() => void load()} />
        )}

        {/* Footer info */}
        <footer class="mt-auto pt-6 text-center text-[10px] text-muted-foreground">
          <div class="flex items-center justify-center gap-1.5">
            <ShieldCheck className="size-3" />
            Pagamento processado via Simulador
          </div>
          <div class="mt-1">
            Após o pagamento, o status pode levar alguns minutos para atualizar.
          </div>
        </footer>
      </div>
    </div>
  );
}

function StatusCard({ charge }: { charge: ChargeData }) {
  if (charge.status === "paid") {
    return (
      <div class="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4 text-center">
        <CheckCircle2 className="mx-auto mb-2 size-8 text-emerald-600 dark:text-emerald-400" />
        <h1 class="text-base font-bold text-emerald-800 dark:text-emerald-300">
          Pagamento confirmado!
        </h1>
        {charge.paidAt && (
          <p class="mt-1 text-xs text-muted-foreground">
            Recebido em {new Date(charge.paidAt).toLocaleString("pt-BR")}.
          </p>
        )}
      </div>
    );
  }
  if (charge.status === "expired") {
    return (
      <div class="rounded-md border border-muted-foreground/30 bg-muted/20 p-4 text-center">
        <Clock className="mx-auto mb-2 size-8 text-muted-foreground" />
        <h1 class="text-base font-bold">Link expirado</h1>
        <p class="mt-1 text-xs text-muted-foreground">
          Peça um novo link à sua escola.
        </p>
      </div>
    );
  }
  if (charge.status === "canceled") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-center">
        <XCircle className="mx-auto mb-2 size-8 text-destructive" />
        <h1 class="text-base font-bold text-destructive">Cobrança cancelada</h1>
        {charge.canceledAt && (
          <p class="mt-1 text-xs text-muted-foreground">
            Cancelada em {new Date(charge.canceledAt).toLocaleString("pt-BR")}.
          </p>
        )}
      </div>
    );
  }
  // pending
  return (
    <div
      class="rounded-md border p-4 text-center"
      style={{
        borderColor: "color-mix(in oklab, var(--brand-primary) 40%, transparent)",
        background: "color-mix(in oklab, var(--brand-primary) 10%, transparent)"
      }}
    >
      <Sparkles
        className="mx-auto mb-2 size-8"
        style={{ color: "var(--brand-primary)" }}
      />
      <h1 class="text-base font-bold">Aguardando pagamento</h1>
      <p class="mt-1 text-xs text-muted-foreground">
        Escaneie o QR ou copie o código Pix abaixo.
      </p>
    </div>
  );
}

function PixBlock({
  pixBrcode,
  txid
}: {
  pixBrcode: string;
  txid: string;
}) {
  const [copied, setCopied] = useState(false);
  const [qrSvg, setQrSvg] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    generateQrWithLogoSvg(pixBrcode, {
      margin: 1,
      dark: "#0f172a",
      light: "#ffffff"
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, [pixBrcode]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(pixBrcode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }
  return (
    <section class="mt-4 rounded-md border border-border bg-card/60 p-5">
      <h2 class="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
        Pague com Pix
      </h2>
      <div class="flex flex-col items-center gap-4">
        {qrSvg ? (
          <div
            class="size-[240px] rounded-md border border-border bg-white p-2 [&_svg]:size-full"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
            aria-label="QR Code Pix"
          />
        ) : (
          <div class="grid size-[240px] place-items-center rounded-md border border-border bg-white text-xs text-muted-foreground">
            Gerando QR…
          </div>
        )}
        <p class="text-[11px] text-muted-foreground">
          Aponte a câmera do app do banco para o QR acima
        </p>
        <div class="w-full">
          <p class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Ou copie e cole no app do banco
          </p>
          <div class="relative">
            <code class="block max-h-32 overflow-y-auto break-all rounded-md border border-border bg-background/60 px-3 py-2 pr-12 font-mono text-[10px] leading-relaxed">
              {pixBrcode}
            </code>
            <button
              type="button"
              onClick={copy}
              title="Copiar Pix"
              aria-label="Copiar Pix"
              class="absolute right-2 top-2 grid size-9 place-items-center rounded-md text-white shadow-md"
              style={{
                background: "var(--brand-primary)",
                borderRadius: "var(--brand-radius)"
              }}
            >
              {copied ? <CheckCircle2 className="size-4" /> : <Copy className="size-4" />}
            </button>
          </div>
          {copied && (
            <p class="mt-1 text-center text-[11px] font-bold text-emerald-700 dark:text-emerald-400">
              Pix copiado!
            </p>
          )}
        </div>
        <details class="w-full text-[10px]">
          <summary class="cursor-pointer text-muted-foreground">
            Identificador da cobrança (txid)
          </summary>
          <code class="mt-1 block break-all rounded border border-border bg-background/40 px-2 py-1 font-mono">
            {txid}
          </code>
        </details>
      </div>
    </section>
  );
}

function Row({
  label,
  value,
  valueClass,
  style
}: {
  label: string;
  value: string;
  valueClass?: string;
  style?: any;
}) {
  return (
    <div class="flex items-baseline justify-between gap-3">
      <dt class="text-xs text-muted-foreground">{label}</dt>
      <dd class={`text-right ${valueClass ?? "text-sm font-semibold"}`} style={style}>
        {value}
      </dd>
    </div>
  );
}

function CardBlock({ slug, onPaid }: { slug: string; onPaid: () => void }) {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [holderDoc, setHolderDoc] = useState("");
  const [cvv, setCvv] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [address, setAddress] = useState<BillingAddress>(emptyBillingAddress);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function pay(e: Event) {
    e.preventDefault();
    const addrErr = validateAddrClient(address);
    if (addrErr) {
      setErrMsg(addrErr);
      return;
    }
    setSubmitting(true);
    setErrMsg(null);
    try {
      const r = await fetch("/api/pay/cc", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          slug,
          number,
          holderName,
          holderDoc,
          cvv,
          expMonth: parseInt(expMonth, 10),
          expYear: parseInt(expYear, 10),
          billingAddress: address
        })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${r.status}`);
      }
      onPaid();
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <section class="mt-4 rounded-md border border-border bg-card/60 p-5">
        <h2 class="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Pagar com cartão
        </h2>
        <p class="mb-3 text-xs text-muted-foreground">
          Aprovação automática. Sem espera, sem QR code.
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          class="w-full rounded-md border px-4 py-2 text-sm font-medium hover:brightness-105"
          style={{
            borderColor:
              "color-mix(in oklab, var(--brand-primary) 40%, transparent)",
            background:
              "color-mix(in oklab, var(--brand-primary) 5%, transparent)",
            color: "var(--brand-primary)"
          }}
        >
          Pagar com cartão de crédito
        </button>
      </section>
    );
  }

  return (
    <section
      class="mt-4 rounded-md border p-5"
      style={{
        borderColor:
          "color-mix(in oklab, var(--brand-primary) 40%, transparent)",
        background:
          "color-mix(in oklab, var(--brand-primary) 5%, transparent)"
      }}
    >
      <h2
        class="mb-3 text-sm font-bold uppercase tracking-wider"
        style={{ color: "var(--brand-primary)" }}
      >
        Cartão de crédito
      </h2>
      <form onSubmit={pay} class="space-y-3">
        {/* Dados do cartão */}
        <fieldset class="space-y-2">
          <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Dados do cartão
          </legend>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Número do cartão
            </label>
            <input
              type="text"
              inputMode="numeric"
              required
              minLength={12}
              maxLength={19}
              value={number}
              onInput={(e) => setNumber((e.target as HTMLInputElement).value)}
              placeholder="4111 1111 1111 1111"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-muted-foreground">
                Mês
              </label>
              <input
                type="number"
                min={1}
                max={12}
                required
                value={expMonth}
                onInput={(e) =>
                  setExpMonth((e.target as HTMLInputElement).value)
                }
                placeholder="MM"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-muted-foreground">
                Ano
              </label>
              <input
                type="number"
                min={2025}
                max={2099}
                required
                value={expYear}
                onInput={(e) =>
                  setExpYear((e.target as HTMLInputElement).value)
                }
                placeholder="AAAA"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                disabled={submitting}
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-muted-foreground">
                CVV
              </label>
              <input
                type="text"
                inputMode="numeric"
                required
                minLength={3}
                maxLength={4}
                value={cvv}
                onInput={(e) => setCvv((e.target as HTMLInputElement).value)}
                placeholder="123"
                title="Código de segurança"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                disabled={submitting}
              />
            </div>
          </div>
        </fieldset>

        {/* Dados do portador */}
        <fieldset class="space-y-2">
          <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Dados do portador
          </legend>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Nome do portador
            </label>
            <input
              type="text"
              required
              value={holderName}
              onInput={(e) =>
                setHolderName((e.target as HTMLInputElement).value)
              }
              placeholder="NOME COMO IMPRESSO"
              title="Nome impresso no cartão"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm uppercase"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              CPF do portador
            </label>
            <input
              type="text"
              inputMode="numeric"
              required
              value={holderDoc}
              onInput={(e) =>
                setHolderDoc((e.target as HTMLInputElement).value)
              }
              placeholder="000.000.000-00"
              title="CPF do portador"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
        </fieldset>

        {/* Endereço de cobrança */}
        <fieldset class="space-y-2">
          <legend class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Endereço de cobrança
          </legend>
          <BillingAddressFields
            value={address}
            onChange={setAddress}
            disabled={submitting}
          />
        </fieldset>

        {errMsg && (
          <div class="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertCircle className="mr-1 inline size-3" />
            {errMsg}
          </div>
        )}
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={submitting}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
            style={{ background: "var(--brand-primary)" }}
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ShieldCheck className="size-4" />
            )}
            Pagar agora
          </button>
        </div>
      </form>
    </section>
  );
}
